import { useMemo, useReducer, useRef } from 'react';

import type { ApprovalStepDraft } from 'ui-product-approvals-common/types/ApprovalData';
import type { ApprovalActor } from 'ui-product-approvals-common/types/ApprovalActor';
import {
  serializeActor,
  type ActorKey,
} from 'ui-product-approvals-common/utils/actorKey';
import {
  addingStepChangesLockedOrder,
  haveLockedSerializedOrdersChanged,
  removingStepChangesLockedOrder,
  stepHasLockedApprover,
} from '../utils/approvalStepsUtils';
import { MAX_STAGES } from '../utils/approvalProcessValidation';

const EMPTY_LOCKED_ACTOR_KEYS: Set<ActorKey> = new Set();
const EMPTY_LOCKED_STAGE_INDEXES: Set<number> = new Set();

interface ApprovalStepsState {
  steps: ApprovalStepDraft[];
  seq: number;
}

type ApprovalStepsAction =
  | { type: 'ADD_STEP'; atIndex?: number }
  | { type: 'REMOVE_STEP'; stepId: string }
  | { type: 'SET_STAGE_ORDER'; steps: ApprovalStepDraft[] }
  | { type: 'ADD_APPROVER'; stepId: string; approver: ApprovalActor }
  | { type: 'REMOVE_APPROVER'; stepId: string; actorKey: ActorKey }
  | { type: 'INIT'; groups: ApprovalActor[][] };

const makeStep = (
  seq: number,
  approvers: ApprovalActor[] = [],
  templateStageLocked = false
): ApprovalStepDraft => ({
  id: `step-${seq}`,
  approvers,
  templateStageLocked,
});

export const initApprovalStepsState = (
  groups: ApprovalActor[][],
  startSeq = 0,
  lockedStageIndexes: Set<number> = EMPTY_LOCKED_STAGE_INDEXES
): ApprovalStepsState => {
  if (groups.length === 0) {
    return { steps: [makeStep(startSeq)], seq: startSeq + 1 };
  }
  return {
    steps: groups.map((approvers, index) =>
      makeStep(startSeq + index, approvers, lockedStageIndexes.has(index))
    ),
    seq: startSeq + groups.length,
  };
};

export const approvalStepsReducer = (
  state: ApprovalStepsState,
  action: ApprovalStepsAction
): ApprovalStepsState => {
  switch (action.type) {
    case 'ADD_STEP': {
      const steps = [...state.steps];
      const atIndex =
        action.atIndex === undefined
          ? steps.length
          : Math.min(Math.max(action.atIndex, 0), steps.length);
      steps.splice(atIndex, 0, makeStep(state.seq));
      return { steps, seq: state.seq + 1 };
    }
    case 'REMOVE_STEP':
      return {
        ...state,
        steps: state.steps.filter((step) => step.id !== action.stepId),
      };
    case 'SET_STAGE_ORDER':
      return { ...state, steps: action.steps };
    case 'ADD_APPROVER':
      return {
        ...state,
        steps: state.steps.map((step) =>
          step.id === action.stepId &&
          !step.approvers.some(
            (approver) =>
              serializeActor(approver.actor) ===
              serializeActor(action.approver.actor)
          )
            ? { ...step, approvers: [...step.approvers, action.approver] }
            : step
        ),
      };
    case 'REMOVE_APPROVER':
      return {
        ...state,
        steps: state.steps.map((step) =>
          step.id === action.stepId
            ? {
                ...step,
                approvers: step.approvers.filter(
                  (approver) =>
                    serializeActor(approver.actor) !== action.actorKey
                ),
              }
            : step
        ),
      };
    case 'INIT':
      return initApprovalStepsState(action.groups, state.seq);
    default:
      return state;
  }
};

export const useApprovalSteps = (
  initialGroups: ApprovalActor[][] = [],
  lockedActorKeys: Set<ActorKey> = EMPTY_LOCKED_ACTOR_KEYS,
  lockedStageIndexes: Set<number> = EMPTY_LOCKED_STAGE_INDEXES,
  stepListLocked = false
) => {
  const [state, dispatch] = useReducer(
    approvalStepsReducer,
    initialGroups,
    (groups) => initApprovalStepsState(groups, 0, lockedStageIndexes)
  );

  // Already-approved approvers (and the steps containing them) are locked: they
  // can't be removed, moved, or have their step order changed. The latest set
  // and state are read through refs so the guards stay correct as
  // approvedApproverActorKeys loads, while keeping the action creators stable.
  const lockedActorKeysRef = useRef(lockedActorKeys);
  // eslint-disable-next-line react-hooks/refs
  lockedActorKeysRef.current = lockedActorKeys;
  const stepListLockedRef = useRef(stepListLocked);
  // eslint-disable-next-line react-hooks/refs
  stepListLockedRef.current = stepListLocked;
  const stateRef = useRef(state);
  // eslint-disable-next-line react-hooks/refs
  stateRef.current = state;

  const actions = useMemo(() => {
    const canRemoveApprover = (stepId: string, actorKey: ActorKey): boolean => {
      const step = stateRef.current.steps.find(
        (currentStep) => currentStep.id === stepId
      );
      if (
        !step ||
        stepListLockedRef.current ||
        lockedActorKeysRef.current.has(actorKey) ||
        step.templateStageLocked ||
        !step.approvers.some(
          (approver) => serializeActor(approver.actor) === actorKey
        )
      ) {
        return false;
      }

      return !haveLockedSerializedOrdersChanged(
        stateRef.current.steps,
        approvalStepsReducer(stateRef.current, {
          type: 'REMOVE_APPROVER',
          stepId,
          actorKey,
        }).steps,
        lockedActorKeysRef.current
      );
    };

    const canRemoveStep = (stepId: string): boolean => {
      if (stepListLockedRef.current) {
        return false;
      }
      const stepIndex = stateRef.current.steps.findIndex(
        (step) => step.id === stepId
      );
      const step = stateRef.current.steps[stepIndex];
      if (!step) {
        return false;
      }
      return !(
        step.templateStageLocked ||
        stepHasLockedApprover(step, lockedActorKeysRef.current) ||
        removingStepChangesLockedOrder(
          stateRef.current.steps,
          stepIndex,
          lockedActorKeysRef.current
        )
      );
    };

    return {
      addStep: (atIndex?: number) => {
        if (stepListLockedRef.current) {
          return;
        }
        if (stateRef.current.steps.length >= MAX_STAGES) {
          return;
        }
        const insertionIndex = Math.min(
          Math.max(atIndex ?? stateRef.current.steps.length, 0),
          stateRef.current.steps.length
        );
        if (
          addingStepChangesLockedOrder(
            stateRef.current.steps,
            insertionIndex,
            lockedActorKeysRef.current
          )
        ) {
          return;
        }
        dispatch({ type: 'ADD_STEP', atIndex: insertionIndex });
      },
      canRemoveStep,
      removeStep: (stepId: string) => {
        if (!canRemoveStep(stepId)) {
          return;
        }
        dispatch({ type: 'REMOVE_STEP', stepId });
      },
      // The builder's reorderable runs are confined by locked stages, so the
      // reported order can never move a locked stage — no per-move veto needed.
      setStageOrder: (steps: ApprovalStepDraft[]) => {
        if (stepListLockedRef.current) {
          return;
        }
        dispatch({ type: 'SET_STAGE_ORDER', steps });
      },
      addApprover: (stepId: string, approver: ApprovalActor) => {
        const step = stateRef.current.steps.find(
          (currentStep) => currentStep.id === stepId
        );
        const action: ApprovalStepsAction = {
          type: 'ADD_APPROVER',
          stepId,
          approver,
        };
        if (
          !step ||
          stepListLockedRef.current ||
          step.templateStageLocked ||
          haveLockedSerializedOrdersChanged(
            stateRef.current.steps,
            approvalStepsReducer(stateRef.current, action).steps,
            lockedActorKeysRef.current
          )
        ) {
          return;
        }
        dispatch(action);
      },
      canRemoveApprover,
      removeApprover: (stepId: string, actorKey: ActorKey) => {
        if (!canRemoveApprover(stepId, actorKey)) {
          return;
        }
        dispatch({ type: 'REMOVE_APPROVER', stepId, actorKey });
      },
      init: (groups: ApprovalActor[][]) => dispatch({ type: 'INIT', groups }),
    };
  }, []);

  return { steps: state.steps, ...actions };
};

export type UseApprovalStepsReturn = ReturnType<typeof useApprovalSteps>;
